import React from "react";
import { Box, IconButton, Select, MenuItem, Typography } from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
} from "@mui/icons-material";

interface PaginationComponentProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  onRowsPerPageChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const PaginationComponent: React.FC<PaginationComponentProps> = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  hasNextPage,
  hasPreviousPage,
}) => {
  const baseOptions = [10, 25, 100];
  const options = baseOptions.includes(rowsPerPage)
    ? baseOptions
    : Array.from(new Set([rowsPerPage, ...baseOptions])).sort((a, b) => a - b);

  const totalPages = Math.ceil(count / rowsPerPage);
  const startItem = count === 0 ? 0 : page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, count);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
        padding: "20px 24px",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fffe 100%)",
        borderRadius: "16px",
        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
        border: "1px solid rgba(132, 225, 204, 0.2)",
        marginTop: "24px",
      }}
    >
      {/* Left: Rows per page */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#64748b",
            letterSpacing: "0.2px",
          }}
        >
          Rows per page:
        </Typography>
        <Select
          value={rowsPerPage}
          onChange={onRowsPerPageChange as any}
          sx={{
            minWidth: "80px",
            height: "40px",
            borderRadius: "10px",
            fontSize: "14px",
            fontWeight: 600,
            color: "#273848",
            background: "#ffffff",
            border: "2px solid #e2e8f0",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&:hover": {
              borderColor: "#84E1CC",
              background: "linear-gradient(135deg, #ffffff 0%, #f0fdf9 100%)",
            },
            "&.Mui-focused": {
              borderColor: "#56c6a9",
              background: "linear-gradient(135deg, #ffffff 0%, #f0fdf9 100%)",
            },
            "& .MuiSelect-icon": {
              color: "#56c6a9",
            },
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option}
              value={option}
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #f0fdf9 0%, #e6f9f4 100%)",
                },
                "&.Mui-selected": {
                  background:
                    "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
                  fontWeight: 600,
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
                  },
                },
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Center: Page info */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#64748b",
            letterSpacing: "0.2px",
          }}
        >
          Showing
          <Box
            component="span"
            sx={{
              color: "#56c6a9",
              fontWeight: 700,
              margin: "0 4px",
            }}
          >
            {startItem}-{endItem}
          </Box>
          of
          <Box
            component="span"
            sx={{
              color: "#56c6a9",
              fontWeight: 700,
              marginLeft: "4px",
            }}
          >
            {count}
          </Box>
        </Typography>
      </Box>

      {/* Right: Navigation buttons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {/* First page button */}
        <IconButton
          onClick={(e) => onPageChange(e, 0)}
          disabled={!hasPreviousPage}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            border: "2px solid #e2e8f0",
            background: "#ffffff",
            transition: "all 0.3s ease",
            "&:hover": {
              borderColor: "#84E1CC",
              background: "linear-gradient(135deg, #f0fdf9 0%, #e6f9f4 100%)",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(132, 225, 204, 0.3)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&.Mui-disabled": {
              background: "#f8fafc",
              borderColor: "#f1f5f9",
              opacity: 0.5,
            },
          }}
        >
          <KeyboardDoubleArrowLeft
            sx={{
              color: !hasPreviousPage ? "#cbd5e1" : "#56c6a9",
              fontSize: 20,
            }}
          />
        </IconButton>

        {/* Previous page button */}
        <IconButton
          onClick={(e) => onPageChange(e, page - 1)}
          disabled={!hasPreviousPage}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            border: "2px solid #e2e8f0",
            background: "#ffffff",
            transition: "all 0.3s ease",
            "&:hover": {
              borderColor: "#84E1CC",
              background: "linear-gradient(135deg, #f0fdf9 0%, #e6f9f4 100%)",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(132, 225, 204, 0.3)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&.Mui-disabled": {
              background: "#f8fafc",
              borderColor: "#f1f5f9",
              opacity: 0.5,
            },
          }}
        >
          <ChevronLeft
            sx={{
              color: !hasPreviousPage ? "#cbd5e1" : "#56c6a9",
              fontSize: 24,
            }}
          />
        </IconButton>

        {/* Page number display */}
        <Box
          sx={{
            padding: "8px 16px",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #56c6a9 0%, #84E1CC 100%)",
            boxShadow: "0 4px 12px rgba(132, 225, 204, 0.3)",
            minWidth: "80px",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: "#ffffff",
              letterSpacing: "0.5px",
            }}
          >
            Page {page + 1} of {totalPages || 1}
          </Typography>
        </Box>

        {/* Next page button */}
        <IconButton
          onClick={(e) => onPageChange(e, page + 1)}
          disabled={!hasNextPage}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            border: "2px solid #e2e8f0",
            background: "#ffffff",
            transition: "all 0.3s ease",
            "&:hover": {
              borderColor: "#84E1CC",
              background: "linear-gradient(135deg, #f0fdf9 0%, #e6f9f4 100%)",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(132, 225, 204, 0.3)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&.Mui-disabled": {
              background: "#f8fafc",
              borderColor: "#f1f5f9",
              opacity: 0.5,
            },
          }}
        >
          <ChevronRight
            sx={{
              color: !hasNextPage ? "#cbd5e1" : "#56c6a9",
              fontSize: 24,
            }}
          />
        </IconButton>

        {/* Last page button */}
        <IconButton
          onClick={(e) => onPageChange(e, totalPages - 1)}
          disabled={!hasNextPage}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            border: "2px solid #e2e8f0",
            background: "#ffffff",
            transition: "all 0.3s ease",
            "&:hover": {
              borderColor: "#84E1CC",
              background: "linear-gradient(135deg, #f0fdf9 0%, #e6f9f4 100%)",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(132, 225, 204, 0.3)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&.Mui-disabled": {
              background: "#f8fafc",
              borderColor: "#f1f5f9",
              opacity: 0.5,
            },
          }}
        >
          <KeyboardDoubleArrowRight
            sx={{
              color: !hasNextPage ? "#cbd5e1" : "#56c6a9",
              fontSize: 20,
            }}
          />
        </IconButton>
      </Box>
    </Box>
  );
};

export default PaginationComponent;
